import { Image } from 'expo-image';
import { useId } from 'react';
import { Platform, StyleSheet } from 'react-native';
import Svg, {
  Defs,
  G,
  Rect,
  Stop,
  LinearGradient as SvgLinearGradient,
} from 'react-native-svg';
import { getTokenValue, XStack, YStack } from 'tamagui';

import { color } from '@/constants/tokens';
import type { Membership } from '@/src/api/membershipsApi';
import PSText from './ui/PSText';

// GeneralSans-Semibold at $veryLarge (48px), web only — see the identical
// override in app/bookings/[id]/index.tsx: Tamagui's web CSS pins line height
// to the font config's `true` (20px) for any size the config doesn't name,
// clipping the glyph. Native sizes the line off the face's own metrics
// instead and doesn't need this.
const PRICE_LINE_HEIGHT = Platform.select({ web: 65 });

// `null` is unlimited and `0` is blocked, not "none left" — see PlanFeature.
export const limitLabel = (limit: number | null) => {
  if (limit === null) return 'Unlimited';
  if (limit === 0) return 'Not included';
  return 'Any ' + String(limit);
};

// Figma's diamond gradients, geometry traced from their SVG exports (nodes
// 2578:6240, 2578:6207) — expo-linear-gradient can't draw a diamond. One
// gradient quad mirrored into four; the viewBox stretches to the card's size.
const QUAD_MIRRORS = [
  'scale(1 1)',
  'scale(1 -1)',
  'scale(-1 1)',
  'scale(-1 -1)',
];

const KIND_LOOK: Record<
  Membership['kind'],
  {
    viewBox: string;
    matrix: string;
    quad: { width: number; height: number };
    opacity: number;
    from: string;
    to: string;
    priceCaption: string;
    badge?: string;
  }
> = {
  access_pass: {
    viewBox: '0 0 323 416',
    matrix: 'matrix(39.5 57.077 -22.169 104.4 0 10.066)',
    quad: { width: 79.644, height: 34.283 },
    opacity: 0.7,
    from: color.brand,
    to: color.brandDark,
    priceCaption: 'per month, cancel anytime.',
  },
  trial: {
    viewBox: '0 0 323 264',
    matrix: 'matrix(36.05 23.461 -19.589 40.152 -47 -26.067)',
    quad: { width: 96.381, height: 49.82 },
    opacity: 0.65,
    from: color.brandSecondary,
    to: color.passGradientStart,
    priceCaption: 'one time purchase.',
    badge: 'Trial',
  },
};

// The viewBox stretches to the card, so the short confirmation card reuses
// this as-is — Figma's 100px-tall variants are this geometry scaled in y.
export const PassGlow = ({ kind }: { kind: Membership['kind'] }) => {
  const look = KIND_LOOK[kind];
  // Web SVG ids are document-global, and the web stack keeps the previous
  // screen mounted under `display: none`. A per-kind id resolved to that
  // hidden copy, which browsers don't paint — glow missing until refresh.
  const gradientId = `passGlow-${useId()}`;
  return (
    <Svg
      width="100%"
      height="100%"
      viewBox={look.viewBox}
      preserveAspectRatio="none"
      style={StyleSheet.absoluteFill}
    >
      <Defs>
        <SvgLinearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="5"
          y2="5"
        >
          <Stop offset="0" stopColor={look.from} />
          <Stop offset="1" stopColor={look.to} />
        </SvgLinearGradient>
      </Defs>
      <G transform={look.matrix} opacity={look.opacity}>
        {QUAD_MIRRORS.map((mirror) => (
          <Rect
            key={mirror}
            {...look.quad}
            fill={`url(#${gradientId})`}
            transform={mirror}
          />
        ))}
      </G>
    </Svg>
  );
};

// Rounded on the two corners that aren't flush — on a card, that's the
// card's own corner it sits in, so the top-right rounding hides under the clip.
export const PlanTag = ({ kind }: { kind: Membership['kind'] }) => {
  const { badge } = KIND_LOOK[kind];
  if (!badge) return null;
  return (
    <YStack
      alignSelf="flex-start"
      px={'$space.md'}
      backgroundColor={'$color.trialBadge'}
      borderBottomLeftRadius={'$radius.base'}
      borderTopRightRadius={'$radius.base'}
    >
      <PSText.Tags color={'$color.textWhite'} fontSize="$base" lineHeight={24}>
        {badge}
      </PSText.Tags>
    </YStack>
  );
};

export const PlanBadge = ({ kind }: { kind: Membership['kind'] }) => (
  <YStack
    position="absolute"
    // No zero space token; the badge sits flush in the corner.
    // eslint-disable-next-line local-rules/no-hardcoded-tokens
    top={0}
    // eslint-disable-next-line local-rules/no-hardcoded-tokens
    right={0}
  >
    <PlanTag kind={kind} />
  </YStack>
);

/**
 * A plan, as every surface that lists plans draws it — home's upsell, the
 * "All plans" list, and the Access Pass landing page. It
 * takes the plan rather than the strings so the icon and the price line can't
 * differ between them.
 */
export default function MembershipPlanCard({
  plan,
  onPress,
  ctaLabel,
  withFeatures = false,
  headerText,
}: {
  plan: Membership;
  onPress: () => void;
  headerText?: string;
  ctaLabel: string;
  /** The perk rows. Off by default: the upsell lists pitch, they don't compare. */
  withFeatures?: boolean;
}) {
  const { priceCaption } = KIND_LOOK[plan.kind];
  return (
    <YStack
      borderWidth={'$borderWidth.hairline'}
      borderColor={'$color.brandDark'}
      // The glow is translucent; this keeps light screens from washing it pink.
      backgroundColor={'$color.brandDark'}
      alignSelf="stretch"
      borderRadius={'$radius.base'}
      overflow="hidden"
      onPress={onPress}
      cursor="pointer"
    >
      <PassGlow kind={plan.kind} />

      <YStack p={'$space.lg'} gap={'$space.md'}>
        <Image
          source={require('@/assets/images/access-pass-logo.png')}
          style={{
            width: 200,
            height: 50,
            marginBottom: getTokenValue('$space.sm'),
          }}
          contentFit="contain"
        />

        {headerText && (
          <PSText.SubTitle color={'$color.textWhite'}>
            {headerText}
          </PSText.SubTitle>
        )}

        <PSText.Body2 color={'$color.textWhite'}>
          {plan.description}
        </PSText.Body2>

        <XStack alignItems="baseline" gap={'$space.xs'}>
          <PSText.Title color={'$color.textWhite'}>
            {plan.priceWithTax.display}
          </PSText.Title>
          <PSText.Body3 flexShrink={1} color={'$color.textWhiteMuted'}>
            {priceCaption}
          </PSText.Body3>
        </XStack>
      </YStack>

      {/* A plan can ship an empty perk list (Premium does on staging), and an
          empty block would just be a gap above the divider. */}
      {withFeatures && plan.planFeatures.length > 0 && (
        <YStack px={'$space.lg'} gap={'$space.lg'} my={'$space.lg'}>
          <PSText.Tags color={'$color.textWhite'}>What you get?</PSText.Tags>
          <YStack
            // p={'$space.md'}
            gap={'$space.md'}
            // borderWidth={'$borderWidth.hairline'}
            // borderColor={'$color.panelTranslucent'}
            // borderRadius={'$radius.base'}
          >
            {plan.planFeatures
              .filter((p) => p.limit !== 0)
              .map((planFeature, index) => (
                <YStack gap={'$space.sm'} key={planFeature.id}>
                  <XStack
                    gap={'$space.md'}
                    justifyContent="space-between"
                    alignItems="center"
                    pt={index > 0 ? '$space.md' : undefined}
                    borderTopWidth={
                      index > 0 ? '$borderWidth.hairline' : undefined
                    }
                    borderTopColor={'$color.panelTranslucent'}
                  >
                    <PSText.Body2 flexShrink={1} color={'$color.textWhite'}>
                      {planFeature.feature.name}
                    </PSText.Body2>
                    <PSText.Body3 color={'$color.textWhiteMuted'}>
                      {limitLabel(planFeature.limit)}
                    </PSText.Body3>
                  </XStack>

                  {planFeature.feature.description && (
                    <PSText.Body3
                      flexShrink={1}
                      color={'$color.textWhiteMuted'}
                    >
                      {planFeature.feature.description}
                    </PSText.Body3>
                  )}
                </YStack>
              ))}
          </YStack>
        </YStack>
      )}

      <XStack
        justifyContent="center"
        p={'$space.md'}
        borderTopColor={'$color.panelTranslucent'}
        borderTopWidth={'$borderWidth.hairline'}
      >
        <PSText.H2 color={'$color.textWhite'}>{ctaLabel}</PSText.H2>
      </XStack>

      <PlanBadge kind={plan.kind} />
    </YStack>
  );
}
